// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, IconProps, IconSource, Space, Styled} from '@supermove/components';
import {
  Form,
  isMutationError,
  responseHasError,
  isValidationError,
  useHover,
  useRef,
  useResponsive,
  useState,
  useToast,
  ResponsiveType,
} from '@supermove/hooks';
import {colors} from '@supermove/styles';
import {ViewStyleProp} from '@supermove/styles/types';

// Design
import FieldInput, {FieldInputProps} from '@shared/design/components/Field/FieldInput';
import FieldValue, {
  FieldValueContentProps,
  FieldValueLabelProps,
} from '@shared/design/components/Field/FieldValue';
import IconButton, {IconButtonProps} from '@shared/design/components/IconButton';
import TextTooltip from '@shared/design/components/TextTooltip';
import ErrorToast from '@shared/design/components/Toast/ErrorToast';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';

const Container = Styled.View<{horizontal?: boolean}>`
  flex-direction: ${({horizontal}) => (horizontal ? 'row' : 'column')};
`;

const TooltipContainer = Styled.View`
  margin-top: 2px;
`;

const LabelContainer = Styled.View<{
  isHorizontal?: boolean;
  isEditing?: boolean;
  responsive: ResponsiveType;
}>`
  margin-right: ${({isHorizontal}) => (isHorizontal ? 8 : 0)}px;
  margin-bottom: ${({isHorizontal}) => (isHorizontal ? 0 : 4)}px;
  margin-top: ${({isEditing, isHorizontal, responsive}) => (isEditing && isHorizontal ? (responsive.desktop ? 6 : 14) : 0)}px;
  justify-content: ${({isHorizontal}) => (isHorizontal ? 'center' : 'flex-start')};
  flex-direction: row;
`;

const ValueContainer = Styled.View<{horizontal?: boolean}>`
  flex-direction: row;
  align-items: center;
  flex: ${({horizontal}) => (horizontal ? 1 : 'auto')};
`;

// Can't center the icon on the parent container because the parent grows and the icon shouldn't
// So create a new container with a fixed height and center the icon in it
const LeftIconContainer = Styled.View`
  align-self: flex-start;
  height: 18px;
  align-items: center;
  justify-content: center;
`;

const EditContainer = Styled.View<{isHorizontal?: boolean}>`
  flex-direction: row;
  align-items: center;
  flex: ${({isHorizontal}) => (isHorizontal ? 1 : 'auto')};
`;

// The padding top is a workaround.
// When the edit is multiple lines, we want it to be at the top.
// But when the edit is a single line, we want it to be centered
// This keeps it almost at the top, but looks centered when it's a single line
const EditButtonContainer = Styled.View<{responsive: ResponsiveType}>`
  flex-direction: row;
  align-self: flex-start;
  padding-top: ${({responsive}) => (responsive.desktop ? 6 : 12)}px;
  flex-shrink: 1;
`;

const EditValueIconContainer = Styled.View`
  align-self: flex-start;
  margin-left: 8px;
`;

const editValueIconSize = 16;

const EditValueIconPlaceholder = Styled.View`
  width: ${editValueIconSize}px;
  height: ${editValueIconSize}px;
`;

const editIconSize = 24;

const Loading = Styled.Loading`
  width: ${editIconSize}px;
  height: ${editIconSize}px;
`;

type EditIconProps = Omit<IconButtonProps, 'source' | 'onPress'>;
type LeftIconProps = Omit<IconProps, 'source'>;
type ToastConfig = {
  displayName: string;
  hideSuccess?: boolean;
  hideError?: boolean;
};
type InputProps = FieldInputProps & {
  setErrors?: Form['setErrors'];
};

export interface EditableFieldProps {
  /** These are the props that are passed into the label component */
  labelProps?: FieldValueLabelProps;
  tooltip?: string;
  /** These props are passed into the FieldValue.Content component */
  valueProps: FieldValueContentProps;
  /** These are the props passed into the FieldInput component,
   * and if input-specific props are needed they should be passed in the `input` field in here */
  inputProps?: InputProps;
  leftIcon?: IconSource;
  leftIconProps?: LeftIconProps;
  leftIconTooltip?: string;
  isDisabled?: boolean;
  editIcon?: IconSource;
  editIconProps?: EditIconProps;
  /** This should return a promise of the API call to have automatic loading behavior.
   * If you need to manually set the loading state, you can create a promise with a ref
   * and return that promise, manually resolving it when loading is complete
   */
  handleSubmit?: () => unknown;
  isHorizontal?: boolean;
  containerStyle?: ViewStyleProp;
  // Not letting this be easily undefined because we usually want to have it
  // If it's really not wanted, we should explicitly set it to null
  responseToasts: ToastConfig | null;
}

const LeftIcon = ({
  leftIcon,
  leftIconProps,
  leftIconTooltip,
  containerStyle,
}: {
  leftIcon?: IconSource;
  leftIconProps?: LeftIconProps;
  leftIconTooltip?: string;
  containerStyle?: ViewStyleProp;
}) => {
  if (!leftIcon) {
    return null;
  }
  return (
    <React.Fragment>
      <TextTooltip text={leftIconTooltip} placement={'top'}>
        <LeftIconContainer style={containerStyle}>
          <Icon source={leftIcon} {...leftIconProps} />
        </LeftIconContainer>
      </TextTooltip>
      <Space width={4} />
    </React.Fragment>
  );
};

const ValueComponent = ({
  valueProps,
  handleEdit,
  editIcon,
  editIconProps,
  isDisabled,
  leftIcon,
  leftIconProps,
  leftIconTooltip,
  isHovered,
  isHorizontal,
  responsive,
}: {
  valueProps: FieldValueContentProps;
  handleEdit: () => void;
  isDisabled?: boolean;
  editIcon: IconSource;
  editIconProps?: EditIconProps;
  leftIcon?: IconSource;
  leftIconProps?: LeftIconProps;
  leftIconTooltip?: string;
  isHovered?: boolean;
  isHorizontal?: boolean;
  responsive: ResponsiveType;
}) => {
  return (
    <ValueContainer horizontal={isHorizontal}>
      <LeftIcon
        leftIcon={leftIcon}
        leftIconProps={leftIconProps}
        leftIconTooltip={leftIconTooltip}
      />
      <FieldValue.Content {...valueProps} />
      <Space width={8} />
      {/* On desktop show on hover, on mobile show always */}
      <EditValueIconContainer>
        {isDisabled || (responsive.desktop && !isHovered) ? (
          <EditValueIconPlaceholder />
        ) : (
          <IconButton
            source={editIcon}
            {...editIconProps}
            buttonStyle={{
              width: editValueIconSize,
              height: editValueIconSize,
              ...editIconProps?.buttonStyle,
            }}
            onPress={handleEdit}
          />
        )}
      </EditValueIconContainer>
    </ValueContainer>
  );
};

const EditComponent = ({
  inputProps,
  handleSubmit,
  handleCancel,
  isLoading,
  isHorizontal,
  leftIcon,
  leftIconProps,
  leftIconTooltip,
  responsive,
}: {
  inputProps: InputProps;
  handleSubmit?: () => void;
  handleCancel: () => void;
  isLoading?: boolean;
  isHorizontal?: boolean;
  leftIcon?: IconSource;
  leftIconProps?: LeftIconProps;
  leftIconTooltip?: string;
  responsive: ResponsiveType;
}) => {
  // Keep track of the initial value to determine if the value changes and to reset if cancelled
  const initialFieldValue = useRef(
    inputProps.value || _.get(inputProps.values, inputProps.name ?? ''),
  );

  const onCancel = () => {
    // Reset this form to the initial state if it's cancelled
    const setValue = inputProps.setFieldValue || inputProps.customSetFieldValue;
    const name = inputProps.name ?? '';
    setValue?.(name, initialFieldValue.current);
    inputProps.setErrors?.({[name]: undefined});
    handleCancel();
  };

  const onSubmit = () => {
    const currentFieldValue = inputProps.value || _.get(inputProps.values, inputProps.name ?? '');
    // Only make the API call if the value has changed
    if (currentFieldValue === initialFieldValue.current) {
      handleCancel();
      return;
    }
    return handleSubmit?.();
  };

  return (
    <EditContainer isHorizontal={isHorizontal}>
      <LeftIcon
        leftIcon={leftIcon}
        leftIconProps={leftIconProps}
        leftIconTooltip={leftIconTooltip}
        containerStyle={{marginTop: responsive.desktop ? 8 : 16}}
      />
      <FieldInput
        isResponsive
        handleEnter={inputProps?.input?.multiline ? undefined : onSubmit}
        handleCommandEnter={onSubmit}
        handleEscape={onCancel}
        {...inputProps}
        input={{autoFocus: true, ...inputProps?.input}}
        style={{flex: 1, ...inputProps?.style}}
      />
      <Space width={8} />
      <EditButtonContainer responsive={responsive}>
        <React.Fragment>
          {isLoading ? (
            <Loading />
          ) : (
            <IconButton
              buttonStyle={{height: editIconSize, width: editIconSize}}
              source={Icon.Check}
              onPress={onSubmit}
            />
          )}
          <Space width={8} />
          <IconButton
            buttonStyle={{height: editIconSize, width: editIconSize}}
            source={Icon.Xmark}
            color={colors.red.warning}
            onPress={onCancel}
          />
        </React.Fragment>
      </EditButtonContainer>
    </EditContainer>
  );
};

const Tooltip = ({tooltip, responsive}: {tooltip: string; responsive: ResponsiveType}) => {
  return (
    <React.Fragment>
      <Space width={8} />
      <TextTooltip text={tooltip} placement={'top'}>
        <TooltipContainer>
          <Icon
            source={Icon.InfoCircle}
            size={responsive.desktop ? 14 : 16}
            color={colors.gray.secondary}
          />
        </TooltipContainer>
      </TextTooltip>
    </React.Fragment>
  );
};

const LabelComponent = ({
  labelProps,
  isHorizontal,
  tooltip,
  responsive,
  isEditing,
}: {
  labelProps: FieldValueLabelProps;
  isHorizontal?: boolean;
  tooltip?: string;
  responsive: ResponsiveType;
  isEditing: boolean;
}) => {
  return (
    <LabelContainer isHorizontal={isHorizontal} isEditing={isEditing} responsive={responsive}>
      <FieldValue.LabelText isResponsive {...labelProps} style={{...labelProps?.style}} />
      {tooltip && <Tooltip tooltip={tooltip} responsive={responsive} />}
    </LabelContainer>
  );
};

const getToastType = (
  response: unknown,
  toastConfig: ToastConfig | null,
): 'SUCCESS' | 'ERROR' | null => {
  if (!toastConfig) {
    return null;
  }
  const hasError = responseHasError(response);
  if (!hasError) {
    if (toastConfig.hideSuccess) {
      return null;
    }
    return 'SUCCESS';
  }
  if (isMutationError(response) && isValidationError(response.data)) {
    // Field handles the validation error
    return null;
  }

  if (toastConfig.hideError) {
    return null;
  }
  return 'ERROR';
};

const EditableField = ({
  labelProps,
  valueProps,
  inputProps,
  isHorizontal,
  leftIcon,
  leftIconProps,
  leftIconTooltip,
  isDisabled,
  editIcon = Icon.Pen,
  editIconProps,
  handleSubmit,
  containerStyle,
  responseToasts: toastConfig,
  tooltip,
}: EditableFieldProps) => {
  const responsive = useResponsive();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {isHovered, ref} = useHover({hoverCheckType: 'enterLeave'});
  const successToast = useToast({
    ToastComponent: SuccessToast,
    message: `${toastConfig?.displayName} updated`,
  });
  const errorToast = useToast({
    ToastComponent: ErrorToast,
    message: `${toastConfig?.displayName} update failed`,
  });

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await handleSubmit?.();

      const toastType = getToastType(response, toastConfig);
      if (toastType === 'SUCCESS') {
        successToast.handleToast();
        setIsEditing(false);
      } else if (toastType === 'ERROR') {
        errorToast.handleToast();
      }
    } catch (error) {
      if (toastConfig && !toastConfig.hideError) {
        errorToast.handleToast();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container ref={ref} style={containerStyle} horizontal={isHorizontal}>
      {labelProps ? (
        <LabelComponent
          labelProps={labelProps}
          isHorizontal={isHorizontal}
          responsive={responsive}
          tooltip={tooltip}
          isEditing={isEditing}
        />
      ) : null}
      {isEditing && inputProps ? (
        <EditComponent
          inputProps={inputProps}
          handleSubmit={onSubmit}
          handleCancel={() => setIsEditing(false)}
          isLoading={isSubmitting}
          isHorizontal={isHorizontal}
          leftIcon={leftIcon}
          leftIconProps={leftIconProps}
          leftIconTooltip={leftIconTooltip}
          responsive={responsive}
        />
      ) : (
        <ValueComponent
          valueProps={valueProps}
          handleEdit={() => setIsEditing(true)}
          editIcon={editIcon}
          editIconProps={editIconProps}
          isDisabled={isDisabled}
          leftIcon={leftIcon}
          leftIconProps={leftIconProps}
          leftIconTooltip={leftIconTooltip}
          isHovered={isHovered}
          isHorizontal={isHorizontal}
          responsive={responsive}
        />
      )}
    </Container>
  );
};

export default EditableField;
